import type { ComponentType } from 'react'
import { ActiveInvitesStatusPage } from '../pages/ActiveInvitesStatusPage'
import { ApiLogsPage } from '../pages/ApiLogsPage'
import { BalanceReportPage } from '../pages/BalanceReportPage'
import {
  ActivePropertiesNoApplicationReportPage,
  ApplicationCompletedFeeNotPaidReportPage,
  ApprovedNotPurchasedReportPage,
  AutoExpireReimbursementsReportPage,
  ChargebackReportPage,
  ClaimNotTimelyReportPage,
  ContractLossRatioReportPage,
  DailyApplicationReportPage,
  DynamicPricingReportPage,
  FailedLoginsReportPage,
  LrForm16ReportPage,
  MissingContactsReportPage,
  MobileEventLogPage,
  MrgLossRatioReportPage,
  NonVerifiedPropertyReportPage,
  OrganicGrowthReportPage,
  PendingReimbursementsReportPage,
  PropertyActivityReportPage,
  W9UploadedReportPage,
} from '../pages/ConfiguredReportPage'
import { DefaultPaymentLogsPage } from '../pages/DefaultPaymentLogsPage'
import {
  FinancialDailyDefaultsReportPage,
  FinancialDailyReportPage,
} from '../pages/FinancialDailyReportPage'
import { ImpersonateLogsPage } from '../pages/ImpersonateLogsPage'
import {
  ImapInboxPage,
  LegacyMailLogsPage,
  MailgunLogsPage,
} from '../pages/MailReportsPage'
import { MessageLogsPage } from '../pages/MessageLogsPage'
import { MrgReportPage } from '../pages/MrgReportPage'
import { PmcPerformanceGreenPage } from '../pages/PmcPerformanceGreenPage'
import { PmcPerformanceRedPage } from '../pages/PmcPerformanceRedPage'
import { TotalNumberOfContractsPage } from '../pages/TotalNumberOfContractsPage'
import { WeeklyReimbursementsReportPage } from '../pages/WeeklyReimbursementsReportPage'
import { VISIBLE_REPORT_HUB_LINKS } from './reportHubLinks'
import type { ReportHubLink, ReportPageRouteEntry } from '../types'

export type { ReportPageRouteEntry } from '../types'

/** React list routes derived from hub (excludes manual routes in `reportRoutes.tsx`). */
const MANUAL_REPORT_ROUTE_PATHS = new Set([
  'payment-logs',
  'default-payment-logs',
  'default-payment-logs/edit/:logId',
  'default-payment-logs/:logId',
  'payment-logs/:logId',
  'payment-logs/detail/:logId',
  'total-number-of-contract/:logId',
  'api-logs/:logId',
  'applicationInvite',
  'api-logs',
  'total-number-of-contract',
  'MRG_Report',
  'financial-mrg',
  'pmc-performance-red',
])

function hubLinkToRouteEntry(link: ReportHubLink): ReportPageRouteEntry | null {
  if (link.kind !== 'react' || !link.reactPath) return null
  return {
    path: link.reactPath,
    policies: link.policies,
    label: link.label,
  }
}

export const REPORT_LIST_ROUTE_ENTRIES: ReportPageRouteEntry[] =
  VISIBLE_REPORT_HUB_LINKS.map(hubLinkToRouteEntry)
    .filter((e): e is ReportPageRouteEntry => e != null)
    .filter((e) => !MANUAL_REPORT_ROUTE_PATHS.has(e.path))

/**
 * Fully implemented report list pages. Others use `ReportPlaceholderPage`.
 */
export const IMPLEMENTED_REPORT_PAGES: Record<string, ComponentType> = {
  applicationInvite: ActiveInvitesStatusPage,
  'api-logs': ApiLogsPage,
  'total-number-of-contract': TotalNumberOfContractsPage,
  'default-payment-logs': DefaultPaymentLogsPage,
  MRG_Report: MrgReportPage,
  'financial-mrg': WeeklyReimbursementsReportPage,
  'pmc-performance-red': PmcPerformanceRedPage,
  'pmc-performance-green': PmcPerformanceGreenPage,
  balances_report: BalanceReportPage,
  'message-logs': MessageLogsPage,
  'impersonate-logs': ImpersonateLogsPage,
  'mailgun-logs': MailgunLogsPage,
  'mail-logs': LegacyMailLogsPage,
  'financial-daily': FinancialDailyReportPage,
  'financial-daily-defaults': FinancialDailyDefaultsReportPage,
  'non-verified-property': NonVerifiedPropertyReportPage,
  'pending-defaults': PendingReimbursementsReportPage,
  'claim-not-timely': ClaimNotTimelyReportPage,
  'expire-defaults': AutoExpireReimbursementsReportPage,
  'active-properties-no-application': ActivePropertiesNoApplicationReportPage,
  chargeback: ChargebackReportPage,
  'missing-contacts': MissingContactsReportPage,
  'imap-logs': ImapInboxPage,
  'mobile-event-log': MobileEventLogPage,
  'failed-logins': FailedLoginsReportPage,
  'daily-application': DailyApplicationReportPage,
  'contract-loss-ratio': ContractLossRatioReportPage,
  'mrg-loss-ratio': MrgLossRatioReportPage,
  'w9-uploaded': W9UploadedReportPage,
  'organic-growth': OrganicGrowthReportPage,
  'approved-not-purchased': ApprovedNotPurchasedReportPage,
  'application-completed-fee-not-paid':
    ApplicationCompletedFeeNotPaidReportPage,
  'property-activity': PropertyActivityReportPage,
  'lr-form-16': LrForm16ReportPage,
  'dynamic-pricing': DynamicPricingReportPage,
}

export const REPORT_REACT_LIST_ROUTES = REPORT_LIST_ROUTE_ENTRIES
